import React, { useContext, useEffect, useRef } from "react";
import { FormControl, FormLabel, Button, Divider, FormErrorMessage, Stack, useBreakpointValue, useColorModeValue as mode, Input } from "@chakra-ui/react";
import { FiKey, FiSave, FiUser } from "react-icons/fi";
import { useNavigate  } from "react-router-dom";
import { AppContext } from "../../App";
import { ApiContext } from "../../contexts/ApiProvider";
import toast from "react-hot-toast";
import { Field, Formik } from "formik";
import { ZitadelContext } from "../../contexts/ZitadelProvider";

interface IPasswordProps {

}

const getPasswordStrength = (password: string): number => {
    var strength = 0;
    if (password.match(/[a-z]+/)){
        strength += 1;
    }
    if (password.match(/[A-Z]+/)){
        strength += 1;
    }
    if (password.match(/[0-9]+/)){
        strength += 1;
    }
    if (password.match(/[$@#&!]+/)){
        strength += 1;
    }
    return strength;
}

const validate = ({ password1, password2 }: { password1: string | undefined, password2: string | undefined }) => {

    const errors: { password1: string | undefined, password2: string | undefined } = { password1: undefined, password2: undefined };
 
    if(!password1 || password1.length < 8) {
        errors.password1 = "Mindestlänge: 8 Zeichen!"
    } else if(getPasswordStrength(password1) < 4) {
        errors.password1 = "Komplexitätsanforderungen nicht erfüllt! (Verwenden Sie Groß- und Kleinbuchstaben sowie mindestens eine Ziffer und ein Sonderzeichen)"
    }   
    else if(password1 !== password2) {
        errors.password2 = "Kennwörter stimmen nicht überein!"
    } else {
        return {};
    }
 
    return errors;

};


export const Password: React.FunctionComponent<IPasswordProps> = (props: IPasswordProps) => {
    const isDesktop = useBreakpointValue({ base: false, lg: true }, { ssr: false });
    const boxShadow = mode('sm', 'sm-dark');

    const navigate = useNavigate();
    const { me, setAppShellConfig } = useContext(AppContext);
    const { patchMePassword } = useContext(ApiContext);
    const { logout } = useContext(ZitadelContext);

    const submitButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        setAppShellConfig({
            breadcrumbs: [{
                text: "Kennwort ändern",
                icon: FiKey
            }]
        });
    }, [setAppShellConfig, me, navigate]);

    return (
        <Formik
            initialValues={{ password1: "", password2: "" }}
            validateOnMount={true}
            onSubmit={(values, formikHelpers) => {
                var promise = patchMePassword({ password: values.password1 });
                toast.promise(promise, {
                    loading: `Kennwort wird geändert...`,
                    success: (result) => {
                        logout();
                        return `Kennwort wurde erfolgreich geändert!`;
                    },
                    error: (code) => {
                        var message = `Fehler beim Ändern des Kennworts! `;
                        switch(code) {
                            case 400: {
                                message += `Fehler 400`;
                                break;
                            }
                            case 404: {
                                message += `Fehler 404`;
                                break;
                            }
                            case 500: {
                                message += `Fehler 500`;
                                break;
                            }
                        }
                        formikHelpers.setSubmitting(false);
                        return message;
                    }
                });
            }}
            validateOnBlur={true}
            validate={validate}
        >
            {({ handleSubmit, errors, touched, setFieldValue, values, isSubmitting, isValid }) => (
                <form onSubmit={handleSubmit}>
                    <Stack 
                        spacing="4" 
                        height="full" 
                        direction="column" 
                        maxW={{ base: undefined, lg: "640px" }} 
                        ml={{ base: undefined, lg: "auto" }}
                        mr={{ base: undefined, lg: "auto" }}
                    >
                        <FormControl isInvalid={errors.password1 ? true : false}>    
                            <FormLabel htmlFor="password1">Neues Kennwort</FormLabel>
                            <Field 
                                as={Input}
                                type="password" 
                                id="password1"
                                name="password1"
                                errors={errors}
                                touched={touched}
                                isDisabled={isSubmitting}  
                            />
                            {errors.password1 && (
                                <FormErrorMessage>{errors.password1}</FormErrorMessage>
                            )}
                        </FormControl>
                        <FormControl isInvalid={errors.password2 ? true : false}>    
                            <FormLabel htmlFor="password2">Neues Kennwort (wiederholen)</FormLabel>
                            <Field 
                                as={Input}
                                type="password" 
                                id="password2"
                                name="password2"
                                errors={errors}
                                touched={touched}
                                isDisabled={isSubmitting}
                            />
                            {errors.password2 && (
                                <FormErrorMessage>{errors.password2}</FormErrorMessage>
                            )}
                        </FormControl>
                        <Divider />
                        <Button 
                            isDisabled={!isValid || isSubmitting}
                            color='red' 
                            variant='outline' 
                            size='lg' 
                            leftIcon={<FiKey />}
                            type="submit"
                        >
                            Kennwort ändern
                        </Button>
                    </Stack>
                </form>
            )}
        </Formik>
    );

}